/**
 * What the Create-ASN wizard takes from the call-off its lines belong to.
 */
import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { callOffAPI } from '../../../lib/api';
import { ASNFormData } from './types';

/**
 * Build supplier-style tracking default: {3-letter prefix}{trailing digits}.
 * Mirrors backend core.utils.call_off_tracking_default — keep both in sync.
 */
function buildSupplierTrackingDefault(supplierName: string | undefined, reference: string | undefined): string {
  if (!supplierName || !reference) return '';
  const tokens = supplierName.split(/\s+/).map((t) => t.replace(/[^A-Za-z]/g, '')).filter((t) => t.length >= 3);
  const prefix = tokens.length
    ? tokens.reduce((a, b) => (b.length > a.length ? b : a)).slice(0, 3).toUpperCase()
    : '';
  const digitsMatch = reference.match(/(\d+)\s*$/);
  const digits = digitsMatch ? digitsMatch[1] : '';
  return prefix && digits ? `${prefix}${digits}` : '';
}

export interface CallOffShipmentDefaults {
  /** CR-8190: the haulier the customer appointed on the call-off. While set it
   *  is the notice's carrier, and the supplier does not choose one. */
  haulierName: string | null;
  /** True while the call-off is being looked up, so the carrier is not known yet. */
  loading: boolean;
  /** True when the lookup failed, so whether the load has a haulier is unknown. */
  failed: boolean;
}

interface CallOffLookup {
  callOffId: number;
  haulierName: string | null;
  failed: boolean;
}

/**
 * Looks up the call-off a notice is being raised for and applies what it
 * decides: the expected delivery date (CR-9120: the haulier's agreed delivery
 * date, else the pickup date) and a tracking-number suggestion fill blanks only,
 * so a supplier's own entries stick; the appointed haulier is returned, for
 * the wizard to show as the carrier.
 *
 * Not built on `useFetchData`: the supplier can move between call-offs while a
 * lookup is in flight, and a late answer for the previous one must not name
 * its haulier on this one. Each lookup is dropped once the call-off changes.
 *
 * @param callOffId - the one call-off the selected lines belong to, or null
 *   when there is none (PO schedules, imported lines, nothing selected)
 * @param setFormData - the wizard's form setter, for the blank-only defaults
 * @returns the appointed haulier's name (null when none), whether the lookup
 *   is still running, and whether it failed
 *
 * @example
 * const { haulierName, loading, failed } = useCallOffShipmentDefaults(callOffId, setFormData);
 */
export function useCallOffShipmentDefaults(
  callOffId: number | null,
  setFormData: Dispatch<SetStateAction<ASNFormData>>,
): CallOffShipmentDefaults {
  const [lookup, setLookup] = useState<CallOffLookup | null>(null);

  useEffect(() => {
    if (callOffId === null) {
      setLookup(null);
      return undefined;
    }
    let cancelled = false;
    callOffAPI.getCallOff(callOffId)
      .then((res) => {
        if (cancelled) return;
        const co = res.data;
        setLookup({ callOffId, haulierName: co.haulier_name || null, failed: false });
        setFormData((prev) => {
          const next = { ...prev };
          // CR-9120: the day the haulier agreed to deliver the load is when
          // the ASN expects it. The customer's pickup date stands in for a
          // load no haulier has dated yet.
          const expectedDelivery = co.haulier_agreed_delivery_date || co.pickup_date;
          if (!prev.expected_delivery_date && expectedDelivery) {
            next.expected_delivery_date = expectedDelivery;
          }
          if (!prev.tracking_number) {
            const suggested = buildSupplierTrackingDefault(co.supplier_name, co.reference);
            if (suggested) next.tracking_number = suggested;
          }
          return next;
        });
      })
      .catch(() => {
        // The supplier fills the fields in and is told the haulier could not
        // be checked; the server still holds a load to its haulier.
        if (!cancelled) setLookup({ callOffId, haulierName: null, failed: true });
      });
    return () => {
      cancelled = true;
    };
  }, [callOffId, setFormData]);

  if (callOffId === null) return { haulierName: null, loading: false, failed: false };
  if (lookup?.callOffId !== callOffId) return { haulierName: null, loading: true, failed: false };
  return { haulierName: lookup.haulierName, loading: false, failed: lookup.failed };
}
